import React, { useContext, useEffect, useState } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  IconButton,
  InputAdornment,
  Alert,
  CircularProgress,
  Stack,
  Divider,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import ClearIcon from "@mui/icons-material/Clear";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import VerifiedUserRoundedIcon from "@mui/icons-material/VerifiedUserRounded";
import AccountTreeRoundedIcon from "@mui/icons-material/AccountTreeRounded";
import SupportAgentRoundedIcon from "@mui/icons-material/SupportAgentRounded";
import logo from "../static/media/login/logo_login.webp";
import main from "../static/media/login/main.webp";
import { AppContext } from "../context/AppContext";

const BLACK = "#000000";
const DARK = "#111111";
const DARK_2 = "#1a1a1a";
const WHITE = "#ffffff";
const ORANGE = "#ff6700";
const ORANGE_DARK = "#d95500";
const TEXT = "#f5f5f5";
const MUTED = "#808080";
const BORDER = "rgba(255,255,255,0.16)";

const inputSx = {
  "& .MuiOutlinedInput-root": {
    backgroundColor: WHITE,
    borderRadius: "14px",
    fontSize: "0.95rem",
    color: DARK,
    transition: "all 0.25s ease",
    "& fieldset": {
      borderColor: "rgba(0,0,0,0.18)",
    },
    "&:hover fieldset": {
      borderColor: ORANGE,
    },
    "&.Mui-focused fieldset": {
      borderColor: ORANGE,
      borderWidth: "1.5px",
    },
  },
  "& .MuiInputBase-input": {
    color: DARK,
    fontWeight: 700,
    "&:-webkit-autofill": {
      WebkitBoxShadow: `0 0 0 100px ${WHITE} inset`,
      WebkitTextFillColor: DARK,
      caretColor: DARK,
      borderRadius: "14px",
    },
  },
  "& .MuiInputBase-input::placeholder": {
    color: MUTED,
    opacity: 1,
  },
  "& .MuiInputLabel-root": {
    color: MUTED,
    fontWeight: 700,
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: ORANGE,
  },
  "& .MuiSvgIcon-root": {
    color: ORANGE,
  },
};

const primaryButtonSx = {
  fontWeight: 800,
  py: 1.35,
  borderRadius: "14px",
  background: `linear-gradient(135deg, ${ORANGE}, ${ORANGE_DARK})`,
  color: WHITE,
  textTransform: "uppercase",
  fontSize: "0.9rem",
  letterSpacing: "0.6px",
  border: `1px solid ${ORANGE}`,
  boxShadow: "none",
  transition: "all 0.25s ease",
  "&:hover": {
    background: `linear-gradient(135deg, ${ORANGE_DARK}, ${BLACK})`,
    boxShadow: "none",
    transform: "translateY(-1px)",
  },
  "&.Mui-disabled": {
    background: "rgba(255,103,0,0.35)",
    color: "rgba(255,255,255,0.75)",
  },
};

const outlineButtonSx = {
  borderColor: "rgba(255,103,0,0.45)",
  color: DARK,
  borderRadius: "14px",
  py: 1.1,
  fontSize: "0.88rem",
  fontWeight: 800,
  textTransform: "uppercase",
  backgroundColor: WHITE,
  boxShadow: "none",
  "&:hover": {
    borderColor: ORANGE,
    backgroundColor: "rgba(255,103,0,0.08)",
    boxShadow: "none",
  },
};

const LoginPage = () => {
  const navigate = useNavigate();

  const {
    logoutUser,
    startLoginSession,
    selectAccount,
    refreshSections,
    refreshBrands,
  } = useContext(AppContext);

  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });

  const [showPassword, setShowPassword] = useState(false);
  const [pendingAccounts, setPendingAccounts] = useState([]);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    document.title = "Login | CMSOLTEC";
    window.scrollTo(0, 0);
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const cleanState = () => {
    setError("");
    setMessage("");
    setPendingAccounts([]);
  };

  const goAfterLogin = async (redirectTo) => {
    if (redirectTo === "/admin-panel") {
      await refreshSections();
      await refreshBrands();
    }

    navigate(redirectTo || "/user-panel", {
      replace: true,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    cleanState();

    const email = String(formData.email || "").trim().toLowerCase();
    const password = String(formData.password || "");

    if (!email || !password) {
      setError("Todos los campos son obligatorios.");
      return;
    }

    setLoading(true);

    try {
      const response = await fetch("/sistema/login/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email,
          password,
        }),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok || !data.ok) {
        logoutUser();
        setError(data.error || data.message || "Credenciales incorrectas.");
        return;
      }

      const result = startLoginSession(data);

      if (!result.ok) {
        logoutUser();
        setError(result.message || "No se pudo iniciar sesión.");
        return;
      }

      if (result.requiresSelection) {
        setPendingAccounts(result.accounts || []);
        setMessage("Seleccione la cuenta con la que desea ingresar.");
        return;
      }

      if (result.account?.user?.requiere_cambio_clave || data.requiere_cambio_clave) {
        navigate("/system/update-password", {
          replace: true,
          state: {
            email: data.email || result.account?.user?.email || email,
          },
        });
        return;
      }

      await goAfterLogin(result.redirectTo || data.redirect_to || "/user-panel");
    } catch {
      logoutUser();
      setError("Error de conexión con el servidor.");
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAccount = async (type) => {
    setError("");
    setMessage("");

    const result = selectAccount(type);

    if (!result.ok) {
      setError(result.message || "No se pudo seleccionar la cuenta.");
      return;
    }

    if (result.account?.user?.requiere_cambio_clave) {
      navigate("/system/update-password", {
        replace: true,
        state: {
          email: result.account?.user?.email || formData.email,
        },
      });
      return;
    }

    await goAfterLogin(result.redirectTo || "/user-panel");
  };

  const resetSelection = () => {
    setPendingAccounts([]);
    setMessage("");
    setError("");
  };

  return (
    <Box
      sx={{
        height: "100dvh",
        width: "100%",
        background:
          "radial-gradient(circle at 12% 16%, rgba(255,103,0,0.13) 0%, rgba(255,103,0,0) 28%), radial-gradient(circle at 88% 10%, rgba(0,0,0,0.11) 0%, rgba(0,0,0,0) 30%), linear-gradient(135deg, #ffffff 0%, #f7f7f7 46%, #ededed 100%)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: { xs: 1.4, md: 4 },
        py: { xs: 1.4, md: 3 },
        position: "relative",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          width: 8,
          height: 8,
          borderRadius: "50%",
          left: "7%",
          top: "15%",
          backgroundColor: ORANGE,
          opacity: 0.35,
        }}
      />

      <Box
        sx={{
          position: "absolute",
          width: 10,
          height: 10,
          borderRadius: "50%",
          right: "8%",
          top: "18%",
          backgroundColor: BLACK,
          opacity: 0.28,
        }}
      />

      <Box
        sx={{
          position: "absolute",
          width: 74,
          height: 74,
          borderRadius: "24px",
          left: "5%",
          bottom: "8%",
          border: "1px solid rgba(255,103,0,0.22)",
        }}
      />

      <Box
        sx={{
          position: "absolute",
          width: 92,
          height: 92,
          borderRadius: "30px",
          right: "15%",
          bottom: "8%",
          border: "1px solid rgba(0,0,0,0.14)",
        }}
      />

      <Box
        sx={{
          position: "absolute",
          left: "13%",
          top: "43%",
          fontSize: "22px",
          color: "rgba(255,103,0,0.32)",
          userSelect: "none",
        }}
      >
        ✦
      </Box>

      <Box
        sx={{
          position: "absolute",
          right: "7%",
          top: "50%",
          fontSize: "30px",
          color: "rgba(0,0,0,0.24)",
          userSelect: "none",
        }}
      >
        ✦
      </Box>

      <Box
        sx={{
          width: "100%",
          maxWidth: "1180px",
          height: { xs: "calc(100dvh - 24px)", md: "min(720px, calc(100dvh - 48px))" },
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1.05fr 0.95fr" },
          borderRadius: { xs: "24px", md: "34px" },
          overflow: "hidden",
          backgroundColor: WHITE,
          border: "1px solid rgba(0,0,0,0.12)",
          boxShadow: "none",
          position: "relative",
          zIndex: 1,
        }}
      >
        <Box
          sx={{
            display: { xs: "none", md: "block" },
            position: "relative",
            overflow: "hidden",
            backgroundColor: "#f2f2f2",
            borderRight: "1px solid rgba(0,0,0,0.10)",
          }}
        >
          <Box
            component="img"
            src={main}
            alt="CMSOLTEC"
            sx={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              objectPosition: "center",
              display: "block",
            }}
          />

          <Box
            sx={{
              position: "absolute",
              inset: 0,
              background:
                "linear-gradient(90deg, rgba(0,0,0,0.04) 0%, rgba(255,255,255,0.18) 100%)",
            }}
          />

          <Box
            sx={{
              position: "absolute",
              left: 28,
              right: 28,
              bottom: 28,
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: 1.4,
            }}
          >
            <Box
              sx={{
                backgroundColor: "rgba(0,0,0,0.78)",
                border: BORDER,
                borderRadius: "20px",
                p: 1.8,
                backdropFilter: "blur(12px)",
                WebkitBackdropFilter: "blur(12px)",
                boxShadow: "none",
              }}
            >
              <VerifiedUserRoundedIcon sx={{ color: ORANGE, fontSize: 25, mb: 0.7 }} />
              <Typography sx={{ color: WHITE, fontWeight: 800, fontSize: "0.82rem", lineHeight: 1.2 }}>
                Acceso seguro
              </Typography>
              <Typography sx={{ color: TEXT, fontWeight: 700, fontSize: "0.72rem", mt: 0.35 }}>
                Credenciales protegidas
              </Typography>
            </Box>

            <Box
              sx={{
                backgroundColor: "rgba(0,0,0,0.78)",
                border: BORDER,
                borderRadius: "20px",
                p: 1.8,
                backdropFilter: "blur(12px)",
                WebkitBackdropFilter: "blur(12px)",
                boxShadow: "none",
              }}
            >
              <AccountTreeRoundedIcon sx={{ color: ORANGE, fontSize: 25, mb: 0.7 }} />
              <Typography sx={{ color: WHITE, fontWeight: 800, fontSize: "0.82rem", lineHeight: 1.2 }}>
                Documentos
              </Typography>
              <Typography sx={{ color: TEXT, fontWeight: 700, fontSize: "0.72rem", mt: 0.35 }}>
                Facturas y servicios
              </Typography>
            </Box>

            <Box
              sx={{
                backgroundColor: "rgba(0,0,0,0.78)",
                border: BORDER,
                borderRadius: "20px",
                p: 1.8,
                backdropFilter: "blur(12px)",
                WebkitBackdropFilter: "blur(12px)",
                boxShadow: "none",
              }}
            >
              <SupportAgentRoundedIcon sx={{ color: ORANGE, fontSize: 25, mb: 0.7 }} />
              <Typography sx={{ color: WHITE, fontWeight: 800, fontSize: "0.82rem", lineHeight: 1.2 }}>
                Soporte
              </Typography>
              <Typography sx={{ color: TEXT, fontWeight: 700, fontSize: "0.72rem", mt: 0.35 }}>
                Licencias y equipos
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            p: { xs: 2, sm: 3, md: 5 },
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: WHITE,
            position: "relative",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              width: "100%",
              maxWidth: "410px",
              position: "relative",
              zIndex: 1,
            }}
          >
            <Box sx={{ textAlign: "center", mb: { xs: 1.8, md: 2.5 } }}>
              <Box
                component="img"
                src={logo}
                alt="CMSOLTEC"
                sx={{
                  width: { xs: "88px", md: "250px" },
                  maxHeight: "72px",
                  objectFit: "contain",
                  mb: { xs: 1.2, md: 1.6 },
                }}
              />

              <Typography
                sx={{
                  color: BLACK,
                  fontWeight: 800,
                  fontSize: { xs: "1.45rem", md: "1.9rem" },
                  letterSpacing: "0",
                  lineHeight: 1.05,
                  mb: 0.7,
                }}
              >
                {pendingAccounts.length === 0 ? "Iniciar Sesión" : "Seleccionar acceso"}
              </Typography>
            </Box>

            {pendingAccounts.length === 0 ? (
              <Box component="form" onSubmit={handleSubmit}>
                {error && (
                  <Alert
                    severity="error"
                    sx={{
                      mb: 1.4,
                      borderRadius: "12px",
                      fontSize: "13px",
                      textAlign: "left",
                      color: "#991b1b",
                      background: "#fff1f2",
                      border: "1px solid #fecdd3",
                      boxShadow: "none",
                      "& .MuiAlert-icon": {
                        color: "#e11d48",
                      },
                    }}
                  >
                    {error}
                  </Alert>
                )}

                {message && (
                  <Alert
                    severity="info"
                    sx={{
                      mb: 1.4,
                      borderRadius: "12px",
                      fontSize: "13px",
                      textAlign: "left",
                      color: DARK,
                      background: "#fff7ed",
                      border: "1px solid rgba(255,103,0,0.28)",
                      boxShadow: "none",
                      "& .MuiAlert-icon": {
                        color: ORANGE,
                      },
                    }}
                  >
                    {message}
                  </Alert>
                )}

                <Stack spacing={1.45}>
                  <TextField
                    label="Correo Electrónico"
                    variant="outlined"
                    fullWidth
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    sx={inputSx}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <EmailOutlinedIcon sx={{ color: ORANGE, fontSize: 20 }} />
                        </InputAdornment>
                      ),
                      endAdornment: formData.email ? (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() =>
                              setFormData({
                                ...formData,
                                email: "",
                              })
                            }
                            edge="end"
                            sx={{
                              color: ORANGE,
                              "&:hover": {
                                backgroundColor: "rgba(255,103,0,0.08)",
                              },
                            }}
                          >
                            <ClearIcon sx={{ fontSize: 19 }} />
                          </IconButton>
                        </InputAdornment>
                      ) : null,
                    }}
                  />

                  <TextField
                    label="Contraseña"
                    type={showPassword ? "text" : "password"}
                    variant="outlined"
                    fullWidth
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    sx={inputSx}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockOutlinedIcon sx={{ color: ORANGE, fontSize: 20 }} />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPassword((prev) => !prev)}
                            edge="end"
                            sx={{
                              color: ORANGE,
                              "&:hover": {
                                backgroundColor: "rgba(255,103,0,0.08)",
                              },
                            }}
                          >
                            {showPassword ? <VisibilityOff sx={{ fontSize: 19 }} /> : <Visibility sx={{ fontSize: 19 }} />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />

                  <Button
                    type="submit"
                    variant="contained"
                    fullWidth
                    disabled={loading}
                    sx={primaryButtonSx}
                  >
                    {loading ? <CircularProgress size={21} sx={{ color: WHITE }} /> : "INGRESAR"}
                  </Button>
                </Stack>

                <Divider sx={{ my: 2, borderColor: "rgba(0,0,0,0.12)" }} />

                <Box sx={{ textAlign: "center" }}>
                  <Button
                    onClick={() => navigate("/")}
                    startIcon={<ArrowBackRoundedIcon />}
                    sx={{
                      mt: 1.1,
                      color: DARK,
                      fontSize: "0.88rem",
                      fontWeight: 800,
                      textTransform: "none",
                      boxShadow: "none",
                      "&:hover": {
                        color: ORANGE,
                        backgroundColor: "transparent",
                        textDecoration: "underline",
                        boxShadow: "none",
                      },
                    }}
                  >
                    Volver al inicio
                  </Button>
                </Box>
              </Box>
            ) : (
              <Box>
                {error && (
                  <Alert
                    severity="error"
                    sx={{
                      mb: 1.4,
                      borderRadius: "12px",
                      fontSize: "13px",
                      textAlign: "left",
                      color: "#991b1b",
                      background: "#fff1f2",
                      border: "1px solid #fecdd3",
                      boxShadow: "none",
                      "& .MuiAlert-icon": {
                        color: "#e11d48",
                      },
                    }}
                  >
                    {error}
                  </Alert>
                )}

                {message && (
                  <Alert
                    severity="info"
                    sx={{
                      mb: 1.4,
                      borderRadius: "12px",
                      fontSize: "13px",
                      textAlign: "left",
                      color: DARK,
                      background: "#fff7ed",
                      border: "1px solid rgba(255,103,0,0.28)",
                      boxShadow: "none",
                      "& .MuiAlert-icon": {
                        color: ORANGE,
                      },
                    }}
                  >
                    {message}
                  </Alert>
                )}

                <Stack spacing={1.25}>
                  {pendingAccounts.map((account) => (
                    <Button
                      key={account.type}
                      variant={account.type === "client" ? "contained" : "outlined"}
                      fullWidth
                      onClick={() => handleSelectAccount(account.type)}
                      sx={account.type === "client" ? primaryButtonSx : outlineButtonSx}
                    >
                      {account.type === "client" ? "MI CUENTA" : "ACCESO DEL SISTEMA"}
                    </Button>
                  ))}

                  <Button
                    onClick={resetSelection}
                    sx={{
                      color: MUTED,
                      fontSize: "13px",
                      fontWeight: 800,
                      textTransform: "none",
                      boxShadow: "none",
                      "&:hover": {
                        backgroundColor: "transparent",
                        color: ORANGE,
                        textDecoration: "underline",
                        boxShadow: "none",
                      },
                    }}
                  >
                    Usar otro correo
                  </Button>

                  <Button
                    onClick={() => navigate("/")}
                    startIcon={<ArrowBackRoundedIcon />}
                    sx={{
                      color: DARK,
                      fontSize: "0.88rem",
                      fontWeight: 800,
                      textTransform: "none",
                      boxShadow: "none",
                      "&:hover": {
                        color: ORANGE,
                        backgroundColor: "transparent",
                        textDecoration: "underline",
                        boxShadow: "none",
                      },
                    }}
                  >
                    Volver al inicio
                  </Button>
                </Stack>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default LoginPage;